import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  Signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { roundTo2Decimals } from '@principle-theorem/accounting';
import {
  TypedFormControl,
  validFormControlChanges$,
} from '@principle-theorem/ng-shared';
import { FeeSchedule } from '@principle-theorem/principle-core';
import {
  IFeeSchedule,
  IPricingRule,
  ITreatmentConfiguration,
  PricingRuleType,
  TreatmentFeeMode,
} from '@principle-theorem/principle-core/interfaces';
import { WithRef } from '@principle-theorem/shared';
import { isNil, isUndefined } from 'lodash';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'pr-edit-base-price',
  templateUrl: './edit-base-price.component.html',
  styleUrls: ['./edit-base-price.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EditBasePriceComponent {
  feeSchedule$ = new ReplaySubject<WithRef<IFeeSchedule>>(1);
  scheduleBasePrice$: Observable<number>;
  config$ = new ReplaySubject<WithRef<ITreatmentConfiguration>>(1);
  price$ = new ReplaySubject<number>(1);
  priceOverride$ = new ReplaySubject<number | undefined>(1);
  pricingRule$ = new ReplaySubject<IPricingRule | undefined>(1);
  canReset$: Observable<boolean>;
  isVariable$: Observable<boolean>;
  displayPrice$: Observable<number>;

  resetDisabled: Signal<boolean>;
  resetButtonTooltip: Signal<string>;
  deleteButtonTooltip: Signal<string>;

  priceCtrl = new TypedFormControl<number>(0, {
    updateOn: 'blur',
  });
  @Input() name = '';
  @Input() tooltip = '';
  @Input() disabled = false;
  @Input({ transform: coerceBooleanProperty }) disableDelete = false;
  @Input({ transform: coerceBooleanProperty }) compact = false;
  @Output() priceOverrideChange = new EventEmitter<number | undefined>();
  @Output() pricingRuleChange = new EventEmitter<IPricingRule>();
  @Output() resetToFeeSchedule = new EventEmitter<void>();
  @Output() basePriceDeleted = new EventEmitter<void>();

  @Input()
  set price(price: number) {
    if (!isNil(price)) {
      this.price$.next(price);
    }
  }

  @Input()
  set priceOverride(priceOverride: number | undefined) {
    this.priceOverride$.next(priceOverride);
  }

  @Input()
  set feeSchedule(feeSchedule: WithRef<IFeeSchedule>) {
    if (feeSchedule) {
      this.feeSchedule$.next(feeSchedule);
    }
  }

  @Input()
  set config(config: WithRef<ITreatmentConfiguration>) {
    if (config) {
      this.config$.next(config);
    }
  }

  @Input()
  set pricingRule(pricingRule: IPricingRule | undefined) {
    this.pricingRule$.next(pricingRule);
  }

  constructor() {
    this.displayPrice$ = combineLatest([this.price$, this.priceOverride$]).pipe(
      map(([price, priceOverride]) =>
        isUndefined(priceOverride) ? price : priceOverride
      )
    );

    this.displayPrice$
      .pipe(takeUntilDestroyed())
      .subscribe((price) =>
        this.priceCtrl.setValue(price, { emitEvent: false })
      );

    validFormControlChanges$(this.priceCtrl)
      .pipe(
        map((value) => roundTo2Decimals(value) ?? 0),
        takeUntilDestroyed()
      )
      .subscribe((price) => this.priceOverrideChange.emit(price));

    this.scheduleBasePrice$ = combineLatest([
      this.feeSchedule$,
      this.config$,
    ]).pipe(
      map(([feeSchedule, config]) =>
        FeeSchedule.getTreatmentFeePrice(feeSchedule, config.ref)
      )
    );

    this.isVariable$ = this.pricingRule$.pipe(
      map((pricingRule) => pricingRule?.type === PricingRuleType.Variable)
    );

    this.canReset$ = combineLatest([
      this.priceOverride$,
      this.pricingRule$,
      this.displayPrice$,
      this.scheduleBasePrice$,
      this.feeSchedule$,
    ]).pipe(
      map(
        ([
          priceOverride,
          pricingRule,
          displayPrice,
          schedulePrice,
          feeSchedule,
        ]) => {
          if (feeSchedule.treatmentFeeMode === TreatmentFeeMode.FixedTotal) {
            return true;
          }

          if (pricingRule?.type === PricingRuleType.Variable) {
            return !isNil(priceOverride);
          }

          return displayPrice !== schedulePrice;
        }
      )
    );

    this.resetDisabled = toSignal(
      combineLatest([this.scheduleBasePrice$, this.priceOverride$]).pipe(
        map(([schedulePrice, priceOverride]) => {
          if (isNil(priceOverride)) {
            return true;
          }

          return schedulePrice === priceOverride;
        })
      ),
      {
        initialValue: false,
      }
    );

    this.resetButtonTooltip = toSignal(
      combineLatest([this.feeSchedule$, this.isVariable$]).pipe(
        map(([feeSchedule, isVariable]) => {
          if (feeSchedule?.treatmentFeeMode === TreatmentFeeMode.FixedTotal) {
            return 'Reset to fixed total calculated base price';
          }

          if (isVariable) {
            return 'Reset to selected pricing option';
          }
          return 'Reset to Fee Schedule price';
        })
      ),
      {
        initialValue: 'Reset to Fee Schedule price',
      }
    );
  }

  resetPrice(): void {
    this.resetToFeeSchedule.emit();
  }

  onPricingRuleChange(updatedPricingRule: IPricingRule): void {
    const selectedItem = updatedPricingRule.ruleItems.find(
      (item) => item.selected
    );

    if (selectedItem?.price !== undefined) {
      this.priceCtrl.setValue(selectedItem.price, { emitEvent: false });
    }

    this.pricingRuleChange.emit(updatedPricingRule);
  }
}
